import { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { requestNotificationPermission, showMessageNotification, loadNotificationSettings, updateBadgeCount } from '../utils/notificationManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
  const { user } = useAuth();
  const [connected, setConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});
  const [incomingCall, setIncomingCall] = useState(null);
  const [callSignals, setCallSignals] = useState([]);
  const wsRef = useRef(null);
  const messageHandlersRef = useRef([]);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const userRef = useRef(user);
  const heartbeatRef = useRef(null);
  const pongReceivedRef = useRef(true);
  const currentChatIdRef = useRef(null);
  const notifSettingsRef = useRef(null);
  const unreadCountRef = useRef(0);

  // Keep userRef in sync
  useEffect(() => { userRef.current = user; }, [user]);

  // Set current chat ID for notification suppression + clear badge
  const setCurrentChatId = useCallback((chatId) => {
    currentChatIdRef.current = chatId;
    if (chatId) {
      // User is viewing a chat, clear the badge
      unreadCountRef.current = 0;
      updateBadgeCount(0);
    }
  }, []);

  // Request notification permission on mount
  useEffect(() => {
    if (user) {
      requestNotificationPermission();
      // Load notification settings
      const token = localStorage.getItem('token');
      if (token) {
        fetch(`${BACKEND_URL}/api/users/notification-settings`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(r => r.ok ? r.json() : null).then(settings => {
          if (settings) {
            notifSettingsRef.current = settings;
            loadNotificationSettings(settings);
          }
        }).catch(() => {});
      }
    }
  }, [user]);

  const addMessageHandler = useCallback((handler) => {
    messageHandlersRef.current.push(handler);
    return () => {
      messageHandlersRef.current = messageHandlersRef.current.filter(h => h !== handler);
    };
  }, []);

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    if (wsRef.current) {
      wsRef.current.onclose = null; // Prevent reconnect on intentional close
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnected(false);
  }, []);

  const connectWebSocket = useCallback(() => {
    const currentUser = userRef.current;
    if (!currentUser) return;
    
    // Don't create multiple connections
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
      return;
    }

    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    
    try {
      const ws = new WebSocket(`${wsUrl}/api/ws/${currentUser.id}`);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[WS] Connected');
        setConnected(true);
        reconnectAttempts.current = 0;
        pongReceivedRef.current = true;
        
        // Start heartbeat
        if (heartbeatRef.current) clearInterval(heartbeatRef.current);
        heartbeatRef.current = setInterval(() => {
          if (!pongReceivedRef.current) {
            // Pong not received — connection is dead, force reconnect
            console.log('[WS] Heartbeat timeout — reconnecting');
            ws.close();
            return;
          }
          pongReceivedRef.current = false;
          try {
            ws.send(JSON.stringify({ type: 'ping' }));
          } catch {
            ws.close();
          }
        }, 25000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Handle pong
          if (data.type === 'pong') {
            pongReceivedRef.current = true;
            return;
          }
          
          // Show notification for new messages from others
          if (data.type === 'new_message' && data.message) {
            const msg = data.message;
            const currentUserId = userRef.current?.id;
            if (msg.sender_id !== currentUserId && msg.sender_id !== 'system') {
              // WhatsApp-style: mark as DELIVERED immediately upon receiving via WS,
              // regardless of whether the chat is currently open. This drives the
              // sender's single → double gray tick transition.
              const token = localStorage.getItem('token');
              if (token && msg.id) {
                fetch(`${BACKEND_URL}/api/messages/${msg.id}/delivered`, {
                  method: 'PUT',
                  headers: { Authorization: `Bearer ${token}` }
                }).catch(() => {});
              }
              const senderName = msg.sender?.display_name || msg.sender_name || msg.sender_display_name || 'Someone';
              showMessageNotification({
                senderName,
                content: msg.content,
                chatId: msg.chat_id,
                chatType: msg.chat_type,
                messageType: msg.message_type,
                isCurrentChat: currentChatIdRef.current === msg.chat_id
              });
              // Update app icon badge count  
              unreadCountRef.current += 1;
              updateBadgeCount(unreadCountRef.current);
            }
          }
          
          // Dispatch to all registered handlers
          messageHandlersRef.current.forEach(handler => {
            try { handler(data); } catch (e) { console.error('[WS] Handler error:', e); }
          });
          
          // Handle typing with auto-timeout
          if (data.type === 'typing') {
            setTypingUsers(prev => ({
              ...prev,
              [data.chat_id]: { ...prev[data.chat_id], [data.user_id]: Date.now() }
            }));
            // Auto-clear after 5 seconds if no stop_typing received
            setTimeout(() => {
              setTypingUsers(prev => {
                const chatTyping = prev[data.chat_id];
                if (chatTyping && chatTyping[data.user_id] && Date.now() - chatTyping[data.user_id] > 4500) {
                  const updated = { ...prev, [data.chat_id]: { ...chatTyping } };
                  delete updated[data.chat_id][data.user_id];
                  if (Object.keys(updated[data.chat_id]).length === 0) delete updated[data.chat_id];
                  return updated;
                }
                return prev;
              });
            }, 5000);
          } else if (data.type === 'stop_typing') {
            setTypingUsers(prev => {
              const updated = { ...prev };
              if (updated[data.chat_id]) {
                delete updated[data.chat_id][data.user_id];
                if (Object.keys(updated[data.chat_id]).length === 0) delete updated[data.chat_id];
              }
              return updated;
            });
          } else if (data.type === 'incoming_call') {
            setIncomingCall(data);
          } else if (data.type === 'call_signal') {
            setCallSignals(prev => [...prev, data]);
          } else if (data.type === 'call_answered' || data.type === 'call_rejected' || data.type === 'call_ended') {
            setIncomingCall(null);
          }
        } catch (e) {
          console.error('[WS] Parse error:', e);
        }
      };

      ws.onerror = () => {};

      ws.onclose = () => {
        console.log('[WS] Disconnected');
        setConnected(false);
        if (heartbeatRef.current) clearInterval(heartbeatRef.current);
        
        // Auto-reconnect if user is still logged in
        if (userRef.current) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 15000);
          reconnectAttempts.current++;
          console.log(`[WS] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current})`);
          reconnectTimeoutRef.current = setTimeout(connectWebSocket, delay);
        }
      };
    } catch (e) {
      console.error('[WS] Connection error:', e);
    }
  }, []);

  // Connect/disconnect based on user auth state
  useEffect(() => {
    if (user) {
      connectWebSocket();
    } else {
      cleanup();
    }
    return cleanup;
  }, [user, connectWebSocket, cleanup]);

  // REST-based heartbeat: keeps is_online=true in DB even across WS reconnects
  // This is critical for multi-instance deployments (AWS ECS) where WS connections
  // may be on different server instances than the status-check API
  const restHeartbeatRef = useRef(null);
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    const sendRestHeartbeat = () => {
      fetch(`${BACKEND_URL}/api/users/heartbeat`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {});
    };

    // Send immediately on mount
    sendRestHeartbeat();
    // Then every 30 seconds
    restHeartbeatRef.current = setInterval(sendRestHeartbeat, 30000);

    return () => {
      if (restHeartbeatRef.current) clearInterval(restHeartbeatRef.current);
    };
  }, [user]);

  // Reconnect when app comes back to foreground (phone screen unlock)
  useEffect(() => {
    const token = localStorage.getItem('token');

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && userRef.current) {
        // Send heartbeat immediately when app becomes visible
        if (token) {
          fetch(`${BACKEND_URL}/api/users/heartbeat`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => {});
        }
        // Check if WS is still alive
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          console.log('[WS] App resumed — reconnecting');
          reconnectAttempts.current = 0;
          connectWebSocket();
        }
      } else if (document.visibilityState === 'hidden' && userRef.current && token) {
        // Mark offline when app goes to background (mobile)
        fetch(`${BACKEND_URL}/api/users/offline`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => {});
      }
    };
    
    const handleOnline = () => {
      if (userRef.current && (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN)) {
        console.log('[WS] Network back — reconnecting');
        reconnectAttempts.current = 0;
        connectWebSocket();
      }
    };

    // Mark offline on page unload (close tab / navigate away)
    const handleBeforeUnload = () => {
      if (userRef.current && token) {
        // Use fetch with keepalive to send the request even as the page unloads
        try {
          fetch(`${BACKEND_URL}/api/users/offline`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            keepalive: true
          });
        } catch {}
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [connectWebSocket]);

  const sendTyping = useCallback((chatId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing', chat_id: chatId }));
    }
  }, []);

  const sendStopTyping = useCallback((chatId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'stop_typing', chat_id: chatId }));
    }
  }, []);

  // Poll for incoming calls as WS fallback (production WS can be unreliable)
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    
    const pollCalls = async () => {
      if (incomingCall) return; // Already handling a call
      try {
        const res = await fetch(`${BACKEND_URL}/api/calls/pending`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const calls = await res.json();
          if (calls.length > 0 && !incomingCall) {
            setIncomingCall({
              type: 'incoming_call',
              call_id: calls[0].call_id,
              from_user: calls[0].from_user,
              call_type: calls[0].call_type
            });
          }
        }
      } catch (e) { /* silent */ }
    };
    
    const interval = setInterval(pollCalls, 3000);
    return () => clearInterval(interval);
  }, [user, incomingCall]);

  const value = {
    connected,
    typingUsers,
    incomingCall,
    callSignals,
    clearCallSignals: () => setCallSignals([]),
    sendTyping,
    sendStopTyping,
    addMessageHandler,
    setIncomingCall,
    setCurrentChatId
  };

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) throw new Error('useWebSocket must be used within a WebSocketProvider');
  return context;
};
